import i18next from 'i18next';
import * as yup from 'yup';

import {
  APPLY_COEFFICIENT,
  COEFFICIENT,
  COMMENT,
  DESCRIPTION, EMAILS,
  ID, INSIGHT_ID,
  IS_COMMENTABLE, ITEMS,
  MA_ID,
  NAME, OPTION_ID, OPTIONS, ORDER_KEY, ORDER_TYPE, POSITION, PRIORITIZED_OPTIONS, PRIORITY, PRIORITY_ITEM, QUESTION_ID,
  QUESTION_TYPE,
  QUESTIONS, RULES, SELECTED_OPTION_ID, SELECTED_OPTION_IDS, SORT, STAKEHOLDER,
  STANDARD_ID,
  STATUS, SUB_TOPIC_ID, TEMPLATE, TEXT_ANSWER, TOPIC, TOPIC_ID,
  VALUE_CHAIN_SECTION,
} from '../../constants/formFields';
import { DESCRIPTION_LENGTH, NAME_MAX_LENGTH, OPTION_MAX_LENGTH } from '../../constants/general';
import { SURVEY_QUESTION_TYPES } from '../../constants/materialAssessment';

import type { TOrders } from '../../constants/interfaces';

export const createSurveySchema = (showStandards: boolean = false) => yup.object({
  [NAME]: yup.string().required(i18next.t(
    'errors.nameRequired',
  )).max(NAME_MAX_LENGTH, i18next.t(
    'errors.maxCharactersLength',
    { name: i18next.t('common.name'), length: NAME_MAX_LENGTH },
  )),
  [DESCRIPTION]: yup.string().required(i18next.t(
    'errors.requiredField',
    { name: i18next.t('materialityAssessment.fields.description') },
  )).max(DESCRIPTION_LENGTH, i18next.t(
    'errors.maxCharactersLength',
    { name: i18next.t('common.description'), length: DESCRIPTION_LENGTH },
  )),
  [MA_ID]: yup.number().required(i18next.t(
    'errors.requiredField',
    { name: i18next.t('materialityAssessment.fields.materialAssessment') },
  )),
  [STANDARD_ID]: showStandards
    ? yup.number().required(i18next.t(
      'errors.requiredField',
      { name: i18next.t('materialityAssessment.fields.standard') },
    ))
    : yup.number().optional(),
  [STATUS]: yup.string().oneOf(['draft', 'done']).optional(),
  [QUESTIONS]: yup.array().of(
    yup.object().shape({
      [ID]: yup.number().optional(),
      [NAME]: yup.string().required(i18next.t(
        'errors.requiredField',
        { name: i18next.t('materialityAssessment.fields.question') },
      )),
      [QUESTION_TYPE]: yup.string()
        .oneOf(['short-answer', 'paragraph', 'single-select', 'multiple-select', 'prioritize'])
        .required(i18next.t(
          'errors.requiredField',
          { name: i18next.t('materialityAssessment.fields.questionType') },
        )),
      [IS_COMMENTABLE]: yup.boolean().required(),
      [OPTIONS]: yup.array().of(
        yup.object().shape({
          [ID]: yup.number().optional(),
          [NAME]: yup.string().required(i18next.t(
            'errors.requiredField',
            { name: i18next.t('materialityAssessment.fields.optionName') },
          )).max(OPTION_MAX_LENGTH, i18next.t(
            'errors.maxCharactersLength',
            { name: i18next.t('common.name'), length: OPTION_MAX_LENGTH },
          )),
        }),
      ).when(QUESTION_TYPE, {
        is: (val: string) => ['single-select', 'multiple-select', 'prioritize'].includes(val),
        then: (optionsSchema) => optionsSchema.min(2, i18next.t(
          'materialityAssessment.fields.options',
        )),
        otherwise: (optionsSchema) => optionsSchema.optional(),
      }),
    }),
  ),
});

export type TCreateSurveySchema = yup.InferType<ReturnType<typeof createSurveySchema>>;

export const dataMASurveyListSchema = yup.object({
  [SORT]: yup.object().shape({
    [ORDER_KEY]: yup.string().required(),
    [ORDER_TYPE]: yup.string<TOrders>(),
  }),
  [STATUS]: yup.string(),
});

export type TDataMASurveyListSchema = yup.InferType<typeof dataMASurveyListSchema>;

export const createInsightAnswerSchema = yup.object({
  [INSIGHT_ID]: yup.number().required(i18next.t('errors.requiredField', {
    name: i18next.t('insightAnswer.insightId'),
  })),
  [ITEMS]: yup.array().of(
    yup.object().shape({
      [QUESTION_ID]: yup.number().required(),
      [QUESTION_TYPE]: yup.string(),

      [TEXT_ANSWER]: yup.string().when(QUESTION_TYPE, {
        is: (val: string) => val === SURVEY_QUESTION_TYPES.SHORT_ANSWER || val === SURVEY_QUESTION_TYPES.PARAGRAPH,
        then: (schema) => schema.required(i18next.t('errors.requiredField', {
          name: i18next.t('materialityAssessment.field'),
        })),
        otherwise: (schema) => schema.optional(),
      }),

      [SELECTED_OPTION_ID]: yup.number().when(QUESTION_TYPE, {
        is: SURVEY_QUESTION_TYPES.SINGLE_SELECT,
        then: (schema) => schema.required(i18next.t(
          'surveyAnswer.selectIsRequired',
          { defaultValue: 'Select is required.' },
        )),
        otherwise: (schema) => schema.optional(),
      }),

      [SELECTED_OPTION_IDS]: yup.array()
        .of(yup.number())
        .transform((value) => (Array.isArray(value) ? value.filter((id) => id !== undefined) : []))
        .when(QUESTION_TYPE, {
          is: SURVEY_QUESTION_TYPES.MULTIPLE_SELECT,
          then: (schema) => schema.min(
            1,
            i18next.t(
              'surveyAnswer.selectIsRequired',
              { defaultValue: 'Select is required.' },
            ),
          ),
          otherwise: (schema) => schema.optional(),
        }),

      [PRIORITIZED_OPTIONS]: yup.array().of(
        yup.object().shape({
          [PRIORITY]: yup.number().required(i18next.t('errors.requiredField', {
            name: i18next.t('materialityAssessment.field'),
          })),
          [OPTION_ID]: yup.number().required(i18next.t('errors.requiredField', {
            name: i18next.t('materialityAssessment.field'),
          })),
        }),
      ).when(QUESTION_TYPE, {
        is: SURVEY_QUESTION_TYPES.PRIORITIZE,
        then: (schema) => schema.required(i18next.t('errors.requiredField', {
          name: i18next.t('materialityAssessment.field'),
        })),
        otherwise: (schema) => schema.optional(),
      }),

      [COMMENT]: yup.string().when(IS_COMMENTABLE, {
        is: true,
        then: (schema) => schema.required(i18next.t('errors.requiredField', {
          name: i18next.t('materialityAssessment.field'),
        })),
        otherwise: (schema) => schema.optional(),
      }),
    }),
  ).required(i18next.t('errors.requiredField', { name: i18next.t('insightAnswer.items') })),

  [RULES]: yup.array().of(
    yup.object().shape({
      [TOPIC_ID]: yup.number().required(i18next.t('errors.requiredField', {
        name: i18next.t('insightAnswer.topicId'),
      })),
      [SUB_TOPIC_ID]: yup.number().required(i18next.t('errors.requiredField', {
        name: i18next.t('insightAnswer.subTopicId'),
      })),
      [POSITION]: yup.number().required(i18next.t('errors.requiredField', {
        name: i18next.t('insightAnswer.position'),
      })),
    }),
  ).optional(),

  [TOPIC]: yup.array().of(yup.number()).min(1, 'At least one topic is required').optional(),
  [SUB_TOPIC_ID]: yup.array(),
});

export type TCreateInsightAnswerSchema = yup.InferType<typeof createInsightAnswerSchema>;

export const surveyShareSchema = yup.object().shape({
  [EMAILS]: yup.array().of(
    yup.object().shape({
      email: yup.string().email('Invalid email format').required('Email is required'),
    }),
  ).min(1, 'At least one email is required').required(),
});

export type TSurveyShareSchema = yup.InferType<typeof surveyShareSchema>;

export const surveyAnswerSubTopicSchema = yup.object().shape({
  [TOPIC]: yup.array().of(yup.number()).min(1, 'At least one topic is required').required(),
  [SUB_TOPIC_ID]: yup.array().of(yup.number()).optional(),
  [PRIORITY]: yup.array().of(yup.number()).optional(),
  [PRIORITY_ITEM]: yup.array().of(yup.number()).optional(),
});

export type TSurveyAnswerSubTopicSchema = yup.InferType<typeof surveyAnswerSubTopicSchema>;

export const emailValidationSchema = yup.object().shape({
  email: yup.string()
    .email(i18next.t('errors.invalidEmail', { defaultValue: 'Invalid email format' }))
    .required(i18next.t('errors.requiredField', {
      name: i18next.t('common.shareModal.email', { defaultValue: 'Email' }),
    })),
});

export type TEmailValidationSchema = yup.InferType<typeof emailValidationSchema>;

export const dataMAInsightsDashboardSchem = yup.object({
  [TEMPLATE]: yup.number(),
  [TOPIC]: yup.number().optional(),
  [COEFFICIENT]: yup.array().optional(),
  [STAKEHOLDER]: yup.array().optional(),
  [APPLY_COEFFICIENT]: yup.boolean().optional(),
  [VALUE_CHAIN_SECTION]: yup.object().shape({
    label: yup.string(),
    value: yup.string(),
  }),
});

export type TMAInsightsDashboardSchema = yup.InferType<typeof dataMAInsightsDashboardSchem>;
